/**
 * Custom hook for session management and validation
 * Provides session restoration and validation functionality
 */

import { useState } from 'react'
import { api } from '@/utils/api-client'
import { useAuthStore } from '@/store/slices/auth-slice'

/**
 * Session data interface matching backend response
 */
export interface SessionData {
  user: any
  workspace: any
  workspaces: any[]
  subscription: any
  sessionId: string
  expiresAt: string
  features?: {
    projectsEnabled: boolean
  }
}

/**
 * Custom hook for session management
 * Provides session validation, restoration, and state management
 * 
 * @returns Session state and actions
 */
export function useSession() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { user, isAuthenticated, setUser, setFeatures, setError: setAuthError } = useAuthStore()

  /**
   * Validates current session with backend
   * Calls /api/session endpoint to check token validity
   * 
   * @returns Promise resolving to session data or throwing error
   */
  const validateSession = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await api.get('/auth/session', {
        requiresAuth: true,
        showErrorToast: false,
      }) 

      if (response.success && response.data) {
        setUser(response.data.user)
        setFeatures(response.data.features ?? { projectsEnabled: false })
        setAuthError(null)
        return response.data
      }

      throw new Error('Invalid session response')
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Session validation failed'
      setError(errorMessage)
      setAuthError(errorMessage)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Note: Removed automatic session validation on mount
   *
   * Previously, this hook would automatically call validateSession() on mount
   * if the user appeared authenticated. This caused unnecessary API calls
   * on public pages (like the homepage) where authentication status is only
   * needed for display purposes, not for access control.
   *
   * Session validation is now handled by:
   * 1. SessionProvider component for protected routes
   * 2. Explicit calls to validateSession() when needed
   * 3. Auth middleware on the server for actual access control
   */

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    validateSession,
  }
}